import { Injectable, Injector, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { environment } from '@gestor-web/environments/environment';
import { UserActivityService } from 'keycloak-angular';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { AuthService } from './auth.service';

const TITULO_AVISO = '⏳ Sessão expirando';

/**
 * Encerra a sessão do usuário inativo (requisito 83 do Padrão TISS).
 *
 * Inatividade é a ausência de interação na aba (mouse, teclado, toque, scroll), medida pelo
 * `UserActivityService` do keycloak-angular; requisição HTTP não conta, senão a consulta
 * periódica de notificações manteria a sessão viva. O logout é marcado para
 * `última atividade + logoutMs`, um horário fixo: aba em segundo plano e computador suspenso
 * atrasam timers, então o prazo é reconferido quando a aba volta a ficar visível e quando
 * chega a próxima atividade. A partir de `avisoMs`, `aviso$` emite o tempo restante a cada
 * segundo e o título da aba muda; qualquer atividade reinicia a contagem.
 */
@Injectable({
  providedIn: 'root',
})
export class InatividadeService implements OnDestroy {
  /** Milissegundos até o logout enquanto o aviso está ativo; `null` fora do aviso. */
  readonly aviso$ = new BehaviorSubject<number | null>(null);

  /** Duração do aviso, do início ao logout. */
  readonly duracaoAvisoMs = environment.inatividade.logoutMs - environment.inatividade.avisoMs;

  private ultimaAtividade: number;
  private atividade: Subscription;
  private contagem: Subscription;
  private tituloAnterior: string;

  constructor(
    private userActivity: UserActivityService,
    private authService: AuthService,
    private title: Title,
    private injector: Injector,
  ) {}

  /**
   * Começa a vigiar a inatividade. Não faz nada se o controle está desligado no environment
   * ou já foi iniciado.
   */
  iniciar(): void {
    if (!environment.inatividade.habilitado || this.atividade) {
      return;
    }
    this.ultimaAtividade = this.userActivity.lastActivityTime;
    this.atividade = toObservable(this.userActivity.lastActivitySignal, { injector: this.injector }).subscribe(momento =>
      this.registrarAtividade(momento),
    );
    document.addEventListener('visibilitychange', this.aoMudarVisibilidade);
    this.agendar();
  }

  /**
   * Para de vigiar a inatividade e esconde o aviso.
   */
  parar(): void {
    this.atividade?.unsubscribe();
    this.atividade = null;
    this.contagem?.unsubscribe();
    document.removeEventListener('visibilitychange', this.aoMudarVisibilidade);
    this.esconderAviso();
  }

  ngOnDestroy(): void {
    this.parar();
  }

  private registrarAtividade(momento: number): void {
    if (this.prazoVencido(momento)) {
      this.encerrar();
      return;
    }
    this.ultimaAtividade = momento;
    this.agendar();
  }

  private agendar(): void {
    this.contagem?.unsubscribe();
    this.esconderAviso();
    const prazo = this.ultimaAtividade + environment.inatividade.logoutMs;
    this.contagem = timer(new Date(this.ultimaAtividade + environment.inatividade.avisoMs), 1000).subscribe(() => {
      const restante = prazo - Date.now();
      if (restante <= 0) {
        this.encerrar();
      } else {
        this.mostrarAviso(restante);
      }
    });
  }

  private readonly aoMudarVisibilidade = () => {
    if (document.visibilityState === 'visible' && this.prazoVencido(Date.now())) {
      this.encerrar();
    }
  };

  private prazoVencido(agora: number): boolean {
    return agora - this.ultimaAtividade >= environment.inatividade.logoutMs;
  }

  private mostrarAviso(restante: number): void {
    if (this.aviso$.value === null) {
      this.tituloAnterior = this.title.getTitle();
      this.title.setTitle(TITULO_AVISO);
    }
    this.aviso$.next(restante);
  }

  private esconderAviso(): void {
    if (this.aviso$.value !== null) {
      this.title.setTitle(this.tituloAnterior);
      this.aviso$.next(null);
    }
  }

  private encerrar(): void {
    this.parar();
    this.authService.logout();
  }
}
